import { Card, Statistic, Row, Col, Empty } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getApps } from '@/api/apps';
import { getProducts } from '@/api/products';
import { getPanels } from '@/api/panels';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getCurrentDeveloperId } from '@/stores/authStore';
import {
  AppstoreOutlined,
  ProductOutlined,
  PictureOutlined
} from '@ant-design/icons';

export const Overview = () => {
  const developerId = getCurrentDeveloperId();
  
  const appQuery = useQuery({
    queryKey: ['apps', developerId],
    queryFn: getApps,
    enabled: !!developerId,
  });
  
  const productQuery = useQuery({
    queryKey: ['products', developerId, 1, 1],
    queryFn: () => getProducts(1, 1),
    enabled: !!developerId,
  });
  
  const panelQuery = useQuery({
    queryKey: ['panels', developerId],
    queryFn: getPanels,
    enabled: !!developerId,
  });

  const loading = appQuery.isLoading || productQuery.isLoading || panelQuery.isLoading;

  if (loading) {
    return <LoadingSpinner />;
  }

  const apps = appQuery.data || [];
  const products = productQuery.data || { total: 0, items: [] };
  const panels = panelQuery.data || [];

  const appCount = apps.length;
  const productCount = products.total;
  const panelCount = panels.length;

  // 优化统计项：增加专属图标、主色值，与侧边栏菜单图标呼应
  const stats = [
    {
      title: 'App SDK',
      value: appCount,
      description: '已创建的应用 SDK',
      icon: <AppstoreOutlined />,
      color: '#165DFF' // 主蓝色
    },
    {
      title: '产品',
      value: productCount,
      description: '已发布的产品',
      icon: <ProductOutlined />,
      color: '#36CFC9' // 青绿色
    },
    {
      title: '面板',
      value: panelCount,
      description: '已上传的控制面板',
      icon: <PictureOutlined />,
      color: '#722ED1' // 紫色
    },
  ];

  return (
    <div className="space-y-8">
      {/* 页面标题 - 优化字号、间距，与后台布局风格统一 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">控制台概况</h1>
        <p className="text-gray-500">查看您的开发者平台资源统计与概览</p>
      </div>
      
      {/* 空状态 - 优化文案为中文、样式、间距，更贴合国内平台使用习惯 */}
      {appCount === 0 && productCount === 0 && panelCount === 0 ? (
        <Empty 
          description={<span className="text-gray-500 text-base">暂无任何资源数据，快去创建您的第一个App SDK吧！</span>}
          className="py-20 bg-white rounded-xl shadow-sm border border-gray-100"
        />
      ) : (
        <Row gutter={[24, 24]}>
          {stats.map((stat, index) => (
            <Col key={index} xs={24} sm={12} lg={8}>
              {/* 统计卡片 - 优化圆角、阴影、hover效果、内边距，增加图标容器 */}
              <Card 
                className="hover:shadow-lg transition-all duration-300 border-0 shadow-sm"
                bodyStyle={{ padding: '24px' }}
                bordered={false}
              >
                <div className="flex items-start justify-between mb-4">
                  {/* 图标容器 - 渐变背景，与统计项主色匹配 */}
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`,
                      color: stat.color
                    }}
                  >
                    {stat.icon}
                  </div>
                  {/* 统计数值 - 优化样式 */}
                  <Statistic
                    value={stat.value}
                    valueStyle={{ 
                      fontSize: '32px', 
                      fontWeight: '700',
                      color: stat.color,
                      lineHeight: '1'
                    }}
                    suffix={null}
                  />
                </div>
                {/* 统计标题和描述 - 单独布局，提升层次感 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{stat.title}</h3>
                  <p className="text-gray-500 text-sm">{stat.description}</p>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};